<template>
  <div>
    <div v-if="loading" class="text-muted">{{ $t('EnduserChat.loading') }}</div>

    <div v-else>
      <!-- 自動非表示バナー -->
      <div v-if="config.auto_disabled_at && !config.is_enabled" class="alert alert-warning">
        ⚠️ ハード上限超過により AI チャットが自動的に非表示になりました（{{ config.auto_disabled_at }}）。
      </div>

      <!-- 保存完了トースト -->
      <div v-if="savedFlash" class="alert alert-success py-2">{{ $t('EnduserChat.saved') }}</div>

      <!-- 表示設定 -->
      <section>
        <h4>{{ $t('EnduserChat.display_settings') }}</h4>
        <hr>
        <div class="mb-3 form-check form-switch">
          <input class="form-check-input" type="checkbox" id="isEnabled" v-model="config.is_enabled">
          <label class="form-check-label" for="isEnabled">
            {{ $t('EnduserChat.is_enabled') }}
            <span :class="config.is_enabled ? 'badge bg-success' : 'badge bg-secondary'">
              {{ config.is_enabled ? $t('EnduserChat.enabled_on') : $t('EnduserChat.enabled_off') }}
            </span>
            <small class="text-muted ms-2">{{ $t('EnduserChat.enabled_hint') }}</small>
          </label>
        </div>
        <div class="mb-3 form-check">
          <input type="checkbox" class="form-check-input" id="autoDisable" v-model="config.auto_disable_on_limit">
          <label class="form-check-label" for="autoDisable">{{ $t('EnduserChat.auto_disable') }}</label>
        </div>
        <div class="mb-3" style="max-width: 480px;">
          <label class="form-label">{{ $t('EnduserChat.source_chat') }}</label>
          <select class="form-select" v-model="config.source_chat_id">
            <option :value="null">{{ $t('EnduserChat.source_chat_unset') }}</option>
            <option v-for="c in chats" :key="c.id" :value="c.id">{{ c.chat_title }}</option>
          </select>
          <small class="text-muted">{{ $t('EnduserChat.source_chat_hint') }}</small>
        </div>
        <div class="mb-3 form-check form-switch">
          <input class="form-check-input" type="checkbox" id="memoryFlag" v-model="config.memory_flag">
          <label class="form-check-label" for="memoryFlag">
            {{ $t('EnduserChat.memory_flag') }}
            <small class="text-muted ms-2">{{ $t('EnduserChat.memory_flag_hint') }}</small>
          </label>
        </div>
        <div class="mb-3">
          <label class="form-label">{{ $t('EnduserChat.greeting_message') }}</label>
          <textarea class="form-control" rows="3" v-model="config.greeting_message"></textarea>
        </div>

        <!-- 固定テキスト（任意編集・未入力なら既定文言） -->
        <div class="mb-3 border rounded p-3 bg-light">
          <div class="fw-bold mb-1">{{ $t('EnduserChat.ui_texts') }}</div>
          <small class="text-muted d-block mb-3">{{ $t('EnduserChat.ui_texts_hint') }}</small>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_header_title') }}</label>
            <input type="text" class="form-control form-control-sm" v-model="config.ui_texts.header_title"
              placeholder="チャットでご案内します！">
          </div>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_menu_prompt') }}</label>
            <input type="text" class="form-control form-control-sm" v-model="config.ui_texts.menu_prompt"
              placeholder="ご質問内容を選択してください。">
          </div>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_composer_prompt') }}</label>
            <input type="text" class="form-control form-control-sm" v-model="config.ui_texts.composer_prompt"
              placeholder="ご質問内容を入力してください。(AIが回答します)">
          </div>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_loading') }}</label>
            <input type="text" class="form-control form-control-sm" v-model="config.ui_texts.loading_message"
              placeholder="回答作成中です。しばらくお待ちください。">
            <small class="text-muted">{{ $t('EnduserChat.txt_loading_hint') }}</small>
          </div>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_no_answer') }}</label>
            <textarea class="form-control form-control-sm" rows="2" v-model="config.ui_texts.no_answer_message"
              placeholder="申し訳ありませんが、提供された参考資料の中に該当する情報は見つかりませんでした。"></textarea>
            <small class="text-muted">{{ $t('EnduserChat.txt_no_answer_hint') }}</small>
          </div>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_feedback_prompt') }}</label>
            <input type="text" class="form-control form-control-sm" v-model="config.ui_texts.feedback_prompt"
              placeholder="解決しましたか？">
          </div>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_feedback_yes') }}</label>
            <input type="text" class="form-control form-control-sm" v-model="config.ui_texts.feedback_yes_message"
              placeholder="お役に立ててよかったです！他にご質問がございましたら、引き続きご入力ください。">
          </div>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_feedback_no') }}</label>
            <input type="text" class="form-control form-control-sm" v-model="config.ui_texts.feedback_no_message"
              placeholder="承知いたしました。引き続きご質問がございましたら、ご入力ください。">
          </div>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_escalate_message') }}</label>
            <input type="text" class="form-control form-control-sm" v-model="config.ui_texts.escalate_message"
              placeholder="お力になれず申し訳ございません。下記フォームよりお問い合わせください。">
            <small class="text-muted">{{ $t('EnduserChat.txt_escalate_message_hint') }}</small>
          </div>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_escalate_link') }}</label>
            <input type="text" class="form-control form-control-sm" v-model="config.ui_texts.escalate_link"
              placeholder="お問い合わせフォームへ →">
            <small class="text-muted">{{ $t('EnduserChat.txt_escalate_link_hint') }}</small>
          </div>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_footer_escalate_prefix') }}</label>
            <input type="text" class="form-control form-control-sm" v-model="config.ui_texts.footer_escalate_prefix"
              placeholder="（空欄でリンクのみ表示）">
            <small class="text-muted">{{ $t('EnduserChat.txt_footer_escalate_prefix_hint') }}</small>
          </div>
          <div class="mb-0">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_no_answer_prompt') }}</label>
            <input type="text" class="form-control form-control-sm" v-model="config.ui_texts.no_answer_prompt"
              placeholder="解決しない場合は、">
          </div>
        </div>

        <!-- 休止モード（AIチャット非表示時）の表示 -->
        <div class="mb-3 border rounded p-3 bg-light">
          <div class="fw-bold mb-1">{{ $t('EnduserChat.dormant_title') }}</div>
          <small class="text-muted d-block mb-3">{{ $t('EnduserChat.dormant_hint') }}</small>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.dormant_message') }}</label>
            <textarea class="form-control form-control-sm" rows="2" v-model="config.ui_texts.dormant_message"
              placeholder="ただいま AI チャットはお休みしております。お問い合わせは下記フォームよりお願いいたします。"></textarea>
          </div>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_disabled_message') }}</label>
            <textarea class="form-control form-control-sm" rows="2" v-model="config.ui_texts.disabled_message"
              placeholder="ただいま AI チャットはご利用を停止しております。お問い合わせは下部のフォームよりお願いいたします。"></textarea>
            <small class="text-muted">{{ $t('EnduserChat.txt_disabled_message_hint') }}</small>
          </div>
          <div class="mb-2">
            <label class="form-label small mb-0">{{ $t('EnduserChat.txt_limit_reached_message') }}</label>
            <textarea class="form-control form-control-sm" rows="2" v-model="config.ui_texts.limit_reached_message"
              placeholder="申し訳ございません。ただいま多くのお問い合わせをいただいており、AI チャットのご利用上限に達しました。お手数ですが、お問い合わせフォームよりご連絡ください。"></textarea>
            <small class="text-muted">{{ $t('EnduserChat.txt_limit_reached_message_hint') }}</small>
          </div>
          <div class="mb-2">
            <label class="form-label small mb-1 d-block">{{ $t('EnduserChat.dormant_links') }}</label>
            <div v-for="(lnk, idx) in config.dormant_links" :key="idx" class="input-group input-group-sm mb-1">
              <input type="text" class="form-control" v-model="lnk.label" style="max-width:200px"
                :placeholder="$t('EnduserChat.dormant_link_label')">
              <input type="url" class="form-control" v-model="lnk.url" placeholder="https://...">
              <button class="btn btn-outline-danger" type="button" @click="removeDormantLink(idx)">×</button>
            </div>
            <button class="btn btn-light btn-sm" type="button" @click="addDormantLink">+ {{ $t('EnduserChat.dormant_add_link') }}</button>
          </div>
          <div class="mb-0">
            <label class="form-label small mb-0">{{ $t('EnduserChat.dormant_footer') }}</label>
            <input type="text" class="form-control form-control-sm" v-model="config.ui_texts.dormant_footer"
              placeholder="解決しない場合は下記よりお問い合わせください。">
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label">{{ $t('EnduserChat.character_image') }}</label>
          <input type="url" class="form-control" v-model="config.character_image_url" placeholder="https://.../character.png">
          <small class="text-muted">{{ $t('EnduserChat.character_image_hint') }}</small>
        </div>
        <div class="mb-3">
          <label class="form-label">{{ $t('EnduserChat.promo_char_image') }}</label>
          <input type="url" class="form-control" v-model="config.ui_texts.promo_char_url" placeholder="https://.../mascot.png">
          <small class="text-muted">{{ $t('EnduserChat.promo_char_image_hint') }}</small>
        </div>
        <div class="mb-3">
          <label class="form-label d-block">{{ $t('EnduserChat.character_size') }}</label>
          <div class="row g-2" style="max-width: 480px;">
            <div class="col-6">
              <div class="input-group input-group-sm">
                <span class="input-group-text">{{ $t('EnduserChat.character_size_pc') }}</span>
                <input type="number" min="20" max="160" class="form-control"
                  v-model.number="config.character_size_pc" placeholder="28">
                <span class="input-group-text">px</span>
              </div>
            </div>
            <div class="col-6">
              <div class="input-group input-group-sm">
                <span class="input-group-text">{{ $t('EnduserChat.character_size_mobile') }}</span>
                <input type="number" min="20" max="160" class="form-control"
                  v-model.number="config.character_size_mobile" placeholder="28">
                <span class="input-group-text">px</span>
              </div>
            </div>
          </div>
          <small class="text-muted">{{ $t('EnduserChat.character_size_hint') }}</small>
        </div>
        <div class="mb-3">
          <label class="form-label d-block">{{ $t('EnduserChat.character_chat_size') }}</label>
          <div class="row g-2" style="max-width: 480px;">
            <div class="col-6">
              <div class="input-group input-group-sm">
                <span class="input-group-text">{{ $t('EnduserChat.character_size_pc') }}</span>
                <input type="number" min="20" max="120" class="form-control"
                  v-model.number="config.character_chat_size_pc" placeholder="30">
                <span class="input-group-text">px</span>
              </div>
            </div>
            <div class="col-6">
              <div class="input-group input-group-sm">
                <span class="input-group-text">{{ $t('EnduserChat.character_size_mobile') }}</span>
                <input type="number" min="20" max="120" class="form-control"
                  v-model.number="config.character_chat_size_mobile" placeholder="30">
                <span class="input-group-text">px</span>
              </div>
            </div>
          </div>
          <small class="text-muted">{{ $t('EnduserChat.character_chat_size_hint') }}</small>
        </div>
        <div class="mb-3">
          <label class="form-label">{{ $t('EnduserChat.contact_form_url') }}</label>
          <input type="url" class="form-control" v-model="config.contact_form_url">
        </div>
        <div class="mb-3" style="max-width: 220px;">
          <label class="form-label">{{ $t('EnduserChat.min_message_length') }}</label>
          <input type="number" min="1" class="form-control" v-model.number="config.min_message_length">
        </div>
      </section>

      <!-- クイックメニュー定義 -->
      <section>
        <h4 class="mt-5">{{ $t('EnduserChat.menu_definitions') }}</h4>
        <hr>
        <div v-for="(menu, idx) in config.menu_definitions" :key="idx" class="card mb-2">
          <div class="card-body py-2">
            <div class="row g-2 align-items-center">
              <div class="col-md-6">
                <label class="form-label small text-muted mb-0">{{ $t('EnduserChat.menu_label') }}</label>
                <input class="form-control form-control-sm" v-model="menu.label" placeholder="例: 店舗について">
              </div>
              <div class="col-md-5">
                <label class="form-label small text-muted mb-0">{{ $t('EnduserChat.menu_source_chat') }}</label>
                <select class="form-select form-select-sm" v-model="menu.source_chat_id">
                  <option :value="null">{{ $t('EnduserChat.menu_source_chat_default') }}</option>
                  <option v-for="c in chats" :key="c.id" :value="c.id">{{ c.chat_title }}</option>
                </select>
              </div>
              <div class="col-md-1 text-end align-self-end">
                <button class="btn btn-sm btn-outline-danger" @click="removeMenu(idx)">×</button>
              </div>
            </div>
            <div class="small text-muted mt-1">ID: <code>{{ menu.key }}</code>（自動採番・会話ログ用）</div>
          </div>
        </div>
        <small class="text-muted d-block mb-2">{{ $t('EnduserChat.menu_source_chat_hint') }}</small>
        <button class="btn btn-light" @click="addMenu">+ {{ $t('EnduserChat.add_menu') }}</button>
      </section>

      <!-- ウィジェット設置（機微情報のため既定は非表示・トグルで開閉） -->
      <section>
        <div class="d-flex align-items-center gap-3 mt-5">
          <h4 class="mb-0">{{ $t('EnduserChat.widget_embed') }}</h4>
          <button class="btn btn-sm" :class="showWidgetEmbed ? 'btn-secondary' : 'btn-outline-secondary'"
            @click="showWidgetEmbed = !showWidgetEmbed">
            {{ showWidgetEmbed ? '🙈 ' + $t('EnduserChat.widget_embed_hide') : '🔒 ' + $t('EnduserChat.widget_embed_show') }}
          </button>
        </div>
        <hr>
        <div v-if="!showWidgetEmbed" class="text-muted small mb-3">
          {{ $t('EnduserChat.widget_embed_secure_hint') }}
        </div>
        <div v-if="showWidgetEmbed">
          <div class="mb-3">
            <label class="form-label">{{ $t('EnduserChat.api_key') }}</label>
            <div class="d-flex align-items-center gap-2">
              <code class="bg-light border rounded px-2 py-1 flex-grow-1">{{ config.widget_api_key }}</code>
              <button class="btn btn-sm btn-light" @click="copyKey">{{ $t('EnduserChat.copy') }}</button>
              <button class="btn btn-sm btn-outline-danger" @click="rotateKey">🔄 {{ $t('EnduserChat.rotate_key') }}</button>
            </div>
          </div>
          <div class="mb-3">
            <label class="form-label">{{ $t('EnduserChat.allowed_origins') }}</label>
            <div class="mb-2">
              <span v-for="(o, i) in config.allowed_origins" :key="i" class="badge bg-primary me-1">
                {{ o }} <a href="#" class="text-white text-decoration-none" @click.prevent="removeOrigin(i)">×</a>
              </span>
            </div>
            <div class="input-group" style="max-width: 480px;">
              <input type="text" class="form-control" v-model="newOrigin" :placeholder="$t('EnduserChat.add_origin')"
                @keyup.enter="addOrigin">
              <button class="btn btn-light" @click="addOrigin">+</button>
            </div>
          </div>
          <div class="mb-3">
            <label class="form-label">{{ $t('EnduserChat.embed_snippet') }}</label>
            <textarea class="form-control font-monospace small" readonly rows="4" :value="embedSnippet"></textarea>
          </div>
        </div>
      </section>

      <!-- 予算アラート -->
      <section>
        <h4 class="mt-5">{{ $t('EnduserChat.budget_alert') }}</h4>
        <hr>
        <div class="row mb-3">
          <div class="col-md-4">
            <label class="form-label">{{ $t('EnduserChat.warning_threshold') }}</label>
            <input type="number" class="form-control" v-model.number="config.point_warning_threshold">
          </div>
          <div class="col-md-4">
            <label class="form-label">{{ $t('EnduserChat.hard_limit') }}</label>
            <input type="number" class="form-control" v-model.number="config.point_hard_limit">
          </div>
        </div>
        <div class="mb-3">
          <label class="form-label">{{ $t('EnduserChat.slack_webhook') }}</label>
          <input type="url" class="form-control" v-model="config.slack_webhook_url"
            placeholder="https://hooks.slack.com/services/...">
        </div>
        <div class="mb-3">
          <label class="form-label">{{ $t('EnduserChat.slack_mentions') }}</label>
          <input type="text" class="form-control" :value="(config.slack_mention_ids || []).join(', ')"
            @input="updateMentions($event.target.value)" placeholder="U01234ABCD, U05678EFGH">
        </div>
        <div class="mb-3">
          <button class="btn btn-outline-secondary btn-sm" @click="testSlack" :disabled="slackTesting">
            📨 {{ $t('EnduserChat.test_slack') }}
          </button>
          <span v-if="slackResult" class="ms-2 small" :class="slackOk ? 'text-success' : 'text-danger'">{{ slackResult }}</span>
        </div>
      </section>

      <!-- アクション -->
      <div class="mt-4 mb-5">
        <div class="d-flex justify-content-end align-items-center gap-2">
          <!-- 保存完了通知（保存ボタンの直近に表示し、見逃しを防ぐ） -->
          <span v-if="savedFlash" class="text-success fw-bold me-2">
            ✅ {{ $t('EnduserChat.saved') }}
          </span>
          <span v-else-if="saveError" class="text-danger fw-bold me-2">
            ⚠️ {{ $t('EnduserChat.save_failed') }}
          </span>
          <button class="btn btn-light" @click="load" :disabled="saving">{{ $t('EnduserChat.discard') }}</button>
          <button class="btn btn-primary" @click="save" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>{{ $t('EnduserChat.save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 公開APIキー再発行の確認モーダル（誤操作防止・入力一致でのみ実行可） -->
    <div v-if="showRotateModal" class="vf-modal-backdrop" @click.self="closeRotateModal">
      <div class="vf-modal card shadow">
        <div class="card-body">
          <h5 class="text-danger">⚠️ {{ $t('EnduserChat.rotate_modal_title') }}</h5>
          <div class="alert alert-warning small mb-3">
            <div class="fw-bold mb-1">{{ $t('EnduserChat.rotate_modal_warn_head') }}</div>
            <ul class="mb-0 ps-3">
              <li>{{ $t('EnduserChat.rotate_modal_warn_1') }}</li>
              <li>{{ $t('EnduserChat.rotate_modal_warn_2') }}</li>
              <li>{{ $t('EnduserChat.rotate_modal_warn_3') }}</li>
            </ul>
          </div>
          <label class="form-label small mb-1">
            {{ $t('EnduserChat.rotate_modal_input_label') }}
            <code class="text-danger">{{ rotateKeyword }}</code>
          </label>
          <input type="text" class="form-control" v-model="rotateConfirmText"
            :placeholder="rotateKeyword" @keyup.enter="rotateMatched && executeRotate()">
          <div class="d-flex justify-content-end gap-2 mt-3">
            <button class="btn btn-light" @click="closeRotateModal">{{ $t('EnduserChat.rotate_modal_cancel') }}</button>
            <button class="btn btn-danger" :disabled="!rotateMatched || rotating" @click="executeRotate">
              🔄 {{ $t('EnduserChat.rotate_modal_execute') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { enduserChatApi, chatsApi } from "@/api";

export default defineComponent({
  name: "EnduserChatSettingView",
  data() {
    return {
      loading: true,
      saving: false,
      savedFlash: false,
      saveError: false,
      // ウィジェット設置情報（APIキー等）は機微情報のため既定で非表示
      showWidgetEmbed: false,
      // 公開APIキー再発行の確認モーダル（誤操作防止）
      showRotateModal: false,
      rotateConfirmText: "",
      rotating: false,
      newOrigin: "",
      config: {},
      chats: [],
      slackTesting: false,
      slackResult: "",
      slackOk: false,
    };
  },
  computed: {
    embedSnippet() {
      const key = this.config.widget_api_key || "YOUR_PUBLIC_KEY";
      // 環境変数の末尾スラッシュ有無に関わらず正しいURLにする
      const base = (process.env.VUE_APP_BACKEND_URL || "https://chat.vectorflux.example/").replace(/\/?$/, "/");
      const close = "</" + "script>";
      return `<script\n  src="${base}widget.js"\n  data-key="${key}"\n  async>${close}`;
    },
    // 再発行を実行するために入力が必要な合言葉
    rotateKeyword() {
      return this.$t("EnduserChat.rotate_keyword");
    },
    rotateMatched() {
      return this.rotateConfirmText.trim() === this.rotateKeyword;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.config = await enduserChatApi.getConfig();
        if (!Array.isArray(this.config.menu_definitions)) this.config.menu_definitions = [];
        // 各メニューに source_chat_id キーを保証（セレクトの既定表示用）
        this.config.menu_definitions.forEach(m => {
          if (m.source_chat_id === undefined) m.source_chat_id = null;
        });
        if (!Array.isArray(this.config.allowed_origins)) this.config.allowed_origins = [];
        if (!Array.isArray(this.config.slack_mention_ids)) this.config.slack_mention_ids = [];
        // 固定テキスト上書き。v-model バインド用に必ずオブジェクトを保証する
        if (!this.config.ui_texts || typeof this.config.ui_texts !== "object") this.config.ui_texts = {};
        // 休止モードのリンクボタン。v-model バインド用に必ず配列を保証する
        if (!Array.isArray(this.config.dormant_links)) this.config.dormant_links = [];
      } catch (e) {
        console.error("failed to load enduser chat config", e);
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      this.saveError = false;
      this.savedFlash = false;
      try {
        const payload = {
          is_enabled: this.config.is_enabled,
          auto_disable_on_limit: this.config.auto_disable_on_limit,
          allowed_origins: this.config.allowed_origins,
          point_warning_threshold: this.config.point_warning_threshold,
          point_hard_limit: this.config.point_hard_limit,
          slack_webhook_url: this.config.slack_webhook_url,
          slack_mention_ids: this.config.slack_mention_ids,
          contact_form_url: this.config.contact_form_url,
          menu_definitions: this.config.menu_definitions,
          character_image_url: this.config.character_image_url,
          character_size_pc: this.config.character_size_pc || null,
          character_size_mobile: this.config.character_size_mobile || null,
          character_chat_size_pc: this.config.character_chat_size_pc || null,
          character_chat_size_mobile: this.config.character_chat_size_mobile || null,
          greeting_message: this.config.greeting_message,
          ui_texts: this.normalizedUiTexts(),
          // 休止モードのリンク：label と url が両方入っている行のみ保存
          dormant_links: (this.config.dormant_links || []).filter(l => l && l.label && l.label.trim() && l.url && l.url.trim()),
          min_message_length: this.config.min_message_length,
          memory_flag: this.config.memory_flag,
          ai_model_id: this.config.ai_model_id,
          source_chat_id: this.config.source_chat_id,
        };
        this.config = await enduserChatApi.updateConfig(payload);
        this.savedFlash = true;
        // 保存完了通知は見逃し防止のため長め（6秒）に表示する
        if (this._savedTimer) clearTimeout(this._savedTimer);
        this._savedTimer = setTimeout(() => { this.savedFlash = false; }, 6000);
      } catch (e) {
        console.error("failed to save enduser chat config", e);
        this.saveError = true;
      } finally {
        this.saving = false;
      }
    },
    // 固定テキスト：空欄のキーは送らない（サーバー/ウィジェット側で既定文言を使う）
    normalizedUiTexts() {
      const src = this.config.ui_texts || {};
      const out = {};
      Object.keys(src).forEach(k => {
        const v = src[k];
        if (typeof v === "string" && v.trim()) out[k] = v.trim();
      });
      return out;
    },
    addDormantLink() {
      if (!Array.isArray(this.config.dormant_links)) this.config.dormant_links = [];
      this.config.dormant_links.push({ label: "", url: "" });
    },
    removeDormantLink(idx) {
      this.config.dormant_links.splice(idx, 1);
    },
    // 1段階目: 確認モーダルを開く（合言葉入力が必要）
    rotateKey() {
      this.rotateConfirmText = "";
      this.showRotateModal = true;
    },
    closeRotateModal() {
      this.showRotateModal = false;
      this.rotateConfirmText = "";
    },
    // 2段階目: 合言葉一致後、最終確認ダイアログを経て実行
    async executeRotate() {
      if (!this.rotateMatched) return;
      if (!window.confirm(this.$t("EnduserChat.rotate_final_confirm"))) return;
      this.rotating = true;
      try {
        const updated = await enduserChatApi.rotateKey();
        this.config.widget_api_key = updated.widget_api_key;
        this.showRotateModal = false;
        this.rotateConfirmText = "";
      } catch (e) {
        console.error("failed to rotate key", e);
      } finally {
        this.rotating = false;
      }
    },
    copyKey() {
      if (navigator.clipboard && this.config.widget_api_key) {
        navigator.clipboard.writeText(this.config.widget_api_key);
      }
    },
    addMenu() {
      // key は内部識別子。会話ログとの整合のため一度採番したら変更しない
      const key = "menu_" + Date.now().toString(36) + Math.floor(Math.random() * 1296).toString(36);
      this.config.menu_definitions.push({ key, label: "", source_chat_id: null });
    },
    removeMenu(idx) {
      this.config.menu_definitions.splice(idx, 1);
    },
    addOrigin() {
      const v = this.newOrigin.trim();
      if (v && !this.config.allowed_origins.includes(v)) {
        this.config.allowed_origins.push(v);
      }
      this.newOrigin = "";
    },
    removeOrigin(i) {
      this.config.allowed_origins.splice(i, 1);
    },
    updateMentions(value) {
      this.config.slack_mention_ids = value.split(",").map(t => t.trim()).filter(t => t);
    },
    async loadChats() {
      try {
        const resp = await chatsApi.getAll();
        this.chats = resp.data || [];
      } catch (e) {
        console.error("failed to load chats", e);
      }
    },
    async testSlack() {
      this.slackTesting = true;
      this.slackResult = "";
      try {
        await enduserChatApi.testSlack();
        this.slackOk = true;
        this.slackResult = "送信しました";
      } catch (e) {
        this.slackOk = false;
        this.slackResult = (e.response && e.response.data && e.response.data.detail) || "送信に失敗しました";
      } finally {
        this.slackTesting = false;
      }
    },
  },
  mounted() {
    this.load();
    this.loadChats();
  },
});
</script>

<style scoped>
.vf-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1080;
  padding: 16px;
}
.vf-modal {
  width: 100%;
  max-width: 460px;
}
</style>
